package com.vamvisions.main.core.data.network

import com.vamvisions.main.api.ApiError
import com.vamvisions.main.core.domain.DataError
import com.vamvisions.main.core.domain.Result
import io.ktor.client.call.NoTransformationFoundException
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.serialization.ContentConvertException
import kotlinx.coroutines.CancellationException
import kotlinx.io.IOException
import kotlinx.serialization.SerializationException

/**
 * Runs [execute] and turns its response, or the exception it throws, into a [Result].
 * Cancellation is rethrown so structured concurrency keeps working.
 */
suspend inline fun <reified T> safeCall(execute: () -> HttpResponse): Result<T, DataError.Network> {
    val response = try {
        execute()
    } catch (e: CancellationException) {
        throw e
    } catch (e: IOException) {
        return networkError(DataError.Network.Type.NO_CONNECTION)
    } catch (e: SerializationException) {
        return networkError(DataError.Network.Type.SERIALIZATION)
    } catch (e: Exception) {
        return networkError(DataError.Network.Type.UNKNOWN)
    } catch (e: Error) {
        // The browser engine reports a failed fetch (offline, DNS, refused connection) as a kotlin.Error.
        return networkError(DataError.Network.Type.NO_CONNECTION)
    }
    return responseToResult(response)
}

suspend inline fun <reified T> responseToResult(response: HttpResponse): Result<T, DataError.Network> =
    when (response.status.value) {
        in 200..299 -> try {
            Result.Success(response.body<T>())
        } catch (e: CancellationException) {
            throw e
        } catch (e: NoTransformationFoundException) {
            networkError(DataError.Network.Type.SERIALIZATION)
        } catch (e: ContentConvertException) {
            networkError(DataError.Network.Type.SERIALIZATION)
        } catch (e: SerializationException) {
            networkError(DataError.Network.Type.SERIALIZATION)
        }
        400 -> networkError(DataError.Network.Type.BAD_REQUEST, response.apiErrorCode())
        404 -> networkError(DataError.Network.Type.NOT_FOUND, response.apiErrorCode())
        429 -> networkError(DataError.Network.Type.TOO_MANY_REQUESTS, response.apiErrorCode())
        in 500..599 -> networkError(DataError.Network.Type.SERVER, response.apiErrorCode())
        else -> networkError(DataError.Network.Type.UNKNOWN, response.apiErrorCode())
    }

@PublishedApi
internal fun networkError(type: DataError.Network.Type, code: String? = null): Result<Nothing, DataError.Network> =
    Result.Error(DataError.Network(type, code))

/** The `code` of an [ApiError] body, or null when the body is missing or is not an [ApiError]. */
@PublishedApi
internal suspend fun HttpResponse.apiErrorCode(): String? = try {
    body<ApiError>().code
} catch (e: CancellationException) {
    throw e
} catch (e: Exception) {
    null
}
